import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

/**
 * LocaleContext — Nexus lightweight i18n (v14).
 *
 * Zero-dep implementation covering the two languages Nexus ships
 * today (Spanish + English). Adding a new locale is a two-file
 * change: (1) add the dictionary under `dictionaries`, (2) add the
 * code to `SUPPORTED_LOCALES`. No component code needs to change.
 *
 * The active locale persists in `localStorage` under `nexus.locale`
 * so a refresh doesn't reset the language. On first load we default
 * to Spanish (product's primary market) but respect the browser
 * `navigator.language` when it matches a supported code.
 */
import { es } from '../i18n/es';
import { en } from '../i18n/en';

export type Locale = 'es' | 'en';
export const SUPPORTED_LOCALES: readonly Locale[] = ['es', 'en'] as const;
export const LOCALE_NAMES: Record<Locale, string> = {
  es: 'Español',
  en: 'English',
};

const dictionaries: Record<Locale, Record<string, unknown>> = { es, en };

interface LocaleContextValue {
  locale: Locale;
  setLocale: (l: Locale) => void;
  t: (key: string, vars?: Record<string, string | number>) => string;
}

const LocaleContext = createContext<LocaleContextValue | null>(null);

function detectInitialLocale(): Locale {
  // Sprint P0 Quirúrgico Feb 2026 · el usuario pidió i18n completo con
  // AUTODETECCIÓN por navegador/dispositivo. Comportamiento:
  //   1) Si el usuario ya eligió idioma (nexus.locale.v2) → respetarlo.
  //   2) Detectar navigator.language / navigator.languages y usar la
  //      primera coincidencia con SUPPORTED_LOCALES. Cualquier variante
  //      'en-*' mapea a 'en', 'es-*' mapea a 'es'.
  //   3) Fallback: 'es' (mercado principal).
  //
  // El selector manual en Perfil sigue disponible y su elección persiste
  // en localStorage — la autodetección solo aplica en el primer render
  // sin preferencia guardada.
  try {
    const saved = window.localStorage.getItem('nexus.locale.v2');
    if (saved === 'es' || saved === 'en') return saved;
  } catch { /* SSR / private mode */ }
  try {
    const nav = typeof navigator !== 'undefined' ? navigator : null;
    if (nav) {
      const langs: string[] = [
        ...(Array.isArray(nav.languages) ? nav.languages : []),
        nav.language || '',
      ].filter(Boolean);
      for (const raw of langs) {
        const code = raw.toLowerCase().slice(0, 2);
        if (code === 'es' || code === 'en') return code as Locale;
      }
    }
  } catch { /* noop */ }
  return 'es';
}

/**
 * Look up a nested key like "profile.language" in the dictionary,
 * falling back to Spanish (primary language) and finally the raw key
 * so we never render `undefined`.
 */
function resolve(
  dict: Record<string, unknown>, key: string,
): string | null {
  const parts = key.split('.');
  let cur: unknown = dict;
  for (const p of parts) {
    if (cur && typeof cur === 'object' && p in (cur as Record<string, unknown>)) {
      cur = (cur as Record<string, unknown>)[p];
    } else {
      return null;
    }
  }
  return typeof cur === 'string' ? cur : null;
}

function interpolate(template: string, vars?: Record<string, string | number>): string {
  if (!vars) return template;
  return template.replace(/\{(\w+)\}/g, (_, k) => (
    k in vars ? String(vars[k]) : `{${k}}`
  ));
}

export const LocaleProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [locale, setLocaleState] = useState<Locale>(detectInitialLocale);

  const setLocale = useCallback((l: Locale) => {
    setLocaleState(l);
    try { window.localStorage.setItem('nexus.locale.v2', l); } catch { /* noop */ }
    // Keep the legacy key in sync for older builds that read it.
    try { window.localStorage.setItem('nexus.locale', l); } catch { /* noop */ }
    // Reflect on <html lang> for a11y + SEO
    if (typeof document !== 'undefined') {
      document.documentElement.setAttribute('lang', l);
    }
  }, []);

  useEffect(() => {
    if (typeof document !== 'undefined') {
      document.documentElement.setAttribute('lang', locale);
    }
  }, [locale]);

  const t = useCallback((key: string, vars?: Record<string, string | number>) => {
    const dict = dictionaries[locale] || dictionaries.es;
    const found = resolve(dict, key);
    if (found != null) return interpolate(found, vars);
    // Fallback chain: primary language → raw key
    const fb = resolve(dictionaries.es, key);
    return fb != null ? interpolate(fb, vars) : key;
  }, [locale]);

  const value = useMemo<LocaleContextValue>(() => ({ locale, setLocale, t }), [locale, setLocale, t]);

  return (
    <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>
  );
};

export function useLocale(): LocaleContextValue {
  const ctx = useContext(LocaleContext);
  if (!ctx) throw new Error('useLocale must be used inside <LocaleProvider>');
  return ctx;
}

/** Convenience alias mirroring the react-i18next API. */
export function useT() {
  return useLocale().t;
}
